import { Input, MenuProps, Tooltip, Typography } from 'antd';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import Menu, { getItem } from '../../../../shared/components/menu/menu';
import PageSpinner from '../../../../shared/components/page-spinner/page-spinner';
import { filterValueByCriteria } from '../../../../shared/utils/filter-value-by-criteria/filter-value-by-criteria';
import { useGetTemplateCollectionsQuery, useGetTemplatesUnassignedQuery } from '../../../templates/api/templates-api';
import { TemplateCollection } from '../../../templates/models/template-collection';

export const SignRequestsSubmenuContent = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation('sign-requests');
  const [searchParams] = useSearchParams();

  const { data: templateCollections, isLoading: isTemplateCollectionsLoading } = useGetTemplateCollectionsQuery();
  const { data: templatesUnassigned, isLoading: isTemplatesUnassignedLoading } = useGetTemplatesUnassignedQuery();

  const [searchCriteria, setSearchCriteria] = useState('');
  const templatesSubmenuKeys = useMemo(
    () => (templateCollections || []).map(templateCollection => templateCollection.name).concat(['templates-list']),
    [templateCollections],
  );
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => setSearchCriteria(event.target.value);

  const filteredUnassignedTemplates = useMemo(
    () =>
      templatesUnassigned &&
      templatesUnassigned.filter(template => filterValueByCriteria(template.name, searchCriteria)),
    [searchCriteria, templatesUnassigned],
  );

  const filteredCollections: TemplateCollection[] | undefined = useMemo(
    () =>
      templateCollections &&
      templateCollections.map(templateCollection => {
        return {
          ...templateCollection,
          templates: templateCollection.templates.filter(template =>
            filterValueByCriteria(template.name, searchCriteria),
          ),
        };
      }),
    [searchCriteria, templateCollections],
  );

  const onOpenChange: MenuProps['onOpenChange'] = keys => {
    const currentOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    if (currentOpenKey !== undefined) {
      const repeatIndex = keys
        .filter(key => key !== currentOpenKey)
        .findIndex(key => templatesSubmenuKeys.includes(key));

      setOpenKeys(keys.filter((_, index) => index !== repeatIndex));
    } else {
      setOpenKeys(keys);
    }
  };

  useEffect(() => {
    const newOpenKeys: string[] = [];

    if (searchCriteria) {
      filteredCollections?.map(collection => {
        if (collection.templates.length > 0 && !openKeys.includes(collection.name)) {
          newOpenKeys.push(collection.name);
        }
      });

      if (
        filteredUnassignedTemplates &&
        filteredUnassignedTemplates.length > 0 &&
        !openKeys.includes('templates-list')
      ) {
        newOpenKeys.push('templates-list');
      }
    }

    if (newOpenKeys.length > 0) {
      setOpenKeys([...openKeys, ...newOpenKeys]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCriteria]);

  useEffect(() => {
    if (!templateCollections || templateCollections.length < 1) {
      setOpenKeys(['templates-list']);
    }
  }, [templateCollections]);

  const noTemplatesFoundItem = () =>
    getItem(
      <Typography.Text type="secondary">{t('signRequestsSubMenu.noTemplatesFound')}</Typography.Text>,
      'no-data',
      undefined,
      undefined,
      'group',
      true,
    );

  const items: MenuProps['items'] = [
    ...[getItem(t('signRequestsSubMenu.allDocuments'), '')],
    ...[
      ...(templateCollections && templateCollections.length > 0
        ? templateCollections.map(templateCollection => {
            const filteredTemplatesByCollection =
              filteredCollections &&
              filteredCollections.find(collection => collection.id === templateCollection.id)?.templates;

            return getItem(
              <Typography.Text strong>{templateCollection.name}</Typography.Text>,
              `${templateCollection.name}`,
              '',
              filteredTemplatesByCollection && filteredTemplatesByCollection.length
                ? filteredTemplatesByCollection.map(template =>
                    getItem(
                      <Tooltip mouseEnterDelay={0.5} title={template.name}>
                        {template.name}
                      </Tooltip>,
                      template.code,
                    ),
                  )
                : [noTemplatesFoundItem()],
            );
          })
        : []),
    ],
    ...[
      ...(templatesUnassigned
        ? [
            getItem(
              <Typography.Text strong>
                {templateCollections && templateCollections.length > 0
                  ? t('signRequestsSubMenu.otherTemplates')
                  : t('signRequestsSubMenu.documentTemplates')}
              </Typography.Text>,
              'templates-list',
              '',

              filteredUnassignedTemplates && filteredUnassignedTemplates.length > 0
                ? filteredUnassignedTemplates.map(template =>
                    getItem(
                      <Tooltip mouseEnterDelay={0.5} title={template.name}>
                        {template.name}
                      </Tooltip>,
                      template.code,
                    ),
                  )
                : [noTemplatesFoundItem()],
            ),
          ]
        : []),
    ],
  ];

  return (
    <>
      <Input size="small" onChange={onSearch} allowClear placeholder={t('signRequestsSubMenu.searchTemplate')} />
      {isTemplatesUnassignedLoading || isTemplateCollectionsLoading ? (
        <PageSpinner />
      ) : (
        <Menu
          items={items}
          onItemSelect={template => {
            searchParams.set('template', template);
            navigate({
              pathname: Routes.SignRequests,
              search: `?${searchParams.toString()}`,
            });
          }}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
        />
      )}
    </>
  );
};

export default SignRequestsSubmenuContent;
